// src/services/queries/match_v2.ts
//
// React-query hooks para Matches do serviço competitions.

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MatchV2, QueryParams } from '@/model';
import { MatchV2Converter } from '@/converters/match_v2';
import { MatchV2Service } from '../instances/match_v2';
import {
  PostMatchV2Request,
  PutMatchV2Request,
  PostMatchesGenerateRequest,
} from '../request/match_v2';

/** GET /matches — lista paginada. */
export const useMatchesV2 = (params?: QueryParams) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['GET_MATCHES_V2', params], [params]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await MatchV2Service.getMatches(params);
      if (err) throw err;
      return {
        data: MatchV2Converter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      } as { data: MatchV2[]; meta: typeof data.meta };
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** GET /matches/:id — detalhe. */
export const useMatchV2ById = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['GET_MATCH_V2_BY_ID', id], [id]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await MatchV2Service.getMatchById(id);
      if (err) throw err;
      return MatchV2Converter.toDomainFromByIdResponse(data);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** POST /matches — cria (idempotente). */
export const usePostMatchV2Mutation = () => {
  return useMutation({
    mutationKey: ['POST_MATCH_V2'],
    mutationFn: async (payload: PostMatchV2Request) => {
      const [err, data] = await MatchV2Service.postMatch(payload);
      if (err) throw err;
      return MatchV2Converter.toDomainFromPostResponse(data);
    },
  });
};

/** PUT /matches/:id — atualiza. */
export const usePutMatchV2Mutation = () => {
  return useMutation({
    mutationKey: ['PUT_MATCH_V2'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutMatchV2Request }) => {
      const [err, data] = await MatchV2Service.putMatch(id, payload);
      if (err) throw err;
      return MatchV2Converter.toDomainFromPutResponse(data);
    },
  });
};

/** DELETE /matches/:id — remove. */
export const useDeleteMatchV2Mutation = () => {
  return useMutation({
    mutationKey: ['DELETE_MATCH_V2'],
    mutationFn: async (id: string) => {
      const [err] = await MatchV2Service.deleteMatch(id);
      if (err) throw err;
    },
  });
};

/** POST /matches/generate — gera calendário automático. */
export const useGenerateMatchesV2Mutation = () => {
  return useMutation({
    mutationKey: ['POST_MATCHES_V2_GENERATE'],
    mutationFn: async (payload: PostMatchesGenerateRequest) => {
      const [err, data] = await MatchV2Service.generateMatches(payload);
      if (err) throw err;
      return MatchV2Converter.toDomainFromGenerateResponse(data);
    },
  });
};
